import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  Optional,
  ViewChildren,
  QueryList,
  HostListener,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CdkMenuTrigger } from '@angular/cdk/menu';

import { Direction } from '@bukio/viewer';
import { ToastService } from 'shared/ui';

import { PDFDrawingService } from '../../services/pdf-drawing.service';
import { UIStateService } from '../../services/ui-state.service';
import { EventBusService } from '../../services/event-bus.service';
import { CdkDragEnd, CdkDragMove } from '@angular/cdk/drag-drop';

enum Tool {
  Selection = 'selection',
  Pen1 = 'pen1',
  Pen2 = 'pen2',
  Pen3 = 'pen3',
  Pen4 = 'pen4',
  Eraser = 'eraser',
  Text = 'text',
}

interface PenConfig {
  id: number;
  width: number;
  opacity: number;
  colorIndex: number;
  isStraightMode: boolean;
  color: string;
  colors: { hex: string }[];
}

interface TextConfig {
  fontSize: number;
  colorIndex: number;
  fontFamily: string;
  color: string;
  colors: { hex: string }[];
}

const DEFAULT_PALETTE = [
  '#021523',
  '#4130A3',
  '#058B8A',
  '#6EFF00',
  '#00F092',
  '#F9C70E',
  '#F5402B',
  '#D62829',
  '#00502D',
  '#002F49',
];

const PALETTE_LOCALSTORAGE_KEY = 'viewer-palette-new';

function alphaToHex(alpha: number): string {
  const alphaValue = Math.round(alpha * 255);
  const hex = alphaValue.toString(16).padStart(2, '0');
  return hex.toUpperCase();
}

@Component({
  selector: 'viewer-drawing-tool-bar',
  templateUrl: './drawing-tool-bar.component.html',
  styleUrl: './drawing-tool-bar.component.scss',
})
export class DrawingToolBarComponent implements OnInit, OnDestroy {
  @HostBinding('class.is-active') isActive = false;
  @ViewChildren(CdkMenuTrigger)
  colorPickerMenuTriggers?: QueryList<CdkMenuTrigger>;

  private unsubscriber = new Subject<void>();

  public _Tool = Tool;
  public _selectedTool?: string;

  public _canUndo = false;
  public _canRedo = false;

  public toolbarPosition: 'left' | 'right' | 'center' = 'right'; // toolbar의 위치 상태를 체크하는 데이터

  /**
   * 팔레트 UI가 리렌더링이 되면 컬러 피커가 닫히기 때문에 색상 변경을 해도 컬러 피커가 닫히지 않게 하기 위해
   * 문자열의 배열이 아닌 객체의 배열로 취급하여 색상이 변경되어도 팔레트 UI가 리랜더링이 일어나지 않도록 함
   */
  public _colors: { hex: string }[] = [];

  public _penConfigs: { [pen: string]: PenConfig } = {
    [Tool.Pen1]: {
      id: 1,
      width: 0.5,
      opacity: 1,
      colorIndex: 0,
      isStraightMode: false,
      color: '#021523',
      colors: [],
    },
    [Tool.Pen2]: {
      id: 2,
      width: 1,
      opacity: 1,
      colorIndex: 1,
      isStraightMode: false,
      color: '#021523',
      colors: [],
    },
    [Tool.Pen3]: {
      id: 3,
      width: 3.5,
      opacity: 0.2,
      colorIndex: 4,
      isStraightMode: false,
      color: '#021523',
      colors: [],
    },
    [Tool.Pen4]: {
      id: 4,
      width: 7,
      opacity: 0.2,
      colorIndex: 6,
      isStraightMode: true,
      color: '#021523',
      colors: [],
    },
  };

  public _selectedPenConfig?: PenConfig;
  public _textConfig: TextConfig = {
    fontSize: 20,
    colorIndex: 0,
    fontFamily: 'Noto Sans KR',
    color: '#000000',
    colors: [],
  };
  public _selectedTextConfig?: TextConfig;

  public _isColorPickerOpened = false;

  // 선택된 텍스트 객체에 대한 색상 변경 UI 표시 여부
  public _selectedTextObjectConfig?: TextConfig;
  public _isTextObjectSelected = false;

  isFontDropdownOpen = false;
  fontOptions = [
    { value: 'Noto Sans KR', label: 'Noto Sans KR' },
    { value: 'Nanum Gothic', label: 'Nanum Gothic' },
    { value: 'Nanum Myeongjo', label: 'Nanum Myeongjo' },
    { value: 'Nanum pen Script', label: 'Nanum pen Script' },
    { value: 'Do Hyeon', label: 'Do Hyeon' },
    { value: 'Jua', label: 'Jua' },
  ];

  constructor(
    private _uiStateService: UIStateService,
    private _eventBusService: EventBusService,
    private _toastService: ToastService,
    @Optional() private _pdfDrawingService?: PDFDrawingService
  ) {
    if (this._loadPalette()) {
      this._penConfigs[Tool.Pen1].colorIndex = 0;
      this._penConfigs[Tool.Pen2].colorIndex = 2;
      this._penConfigs[Tool.Pen3].colorIndex = 8;
      this._penConfigs[Tool.Pen4].colorIndex = 5;
    }
  }

  ngOnInit(): void {
    const savedPenConfigs = localStorage.getItem('penConfigs-new');

    if (savedPenConfigs) {
      this._penConfigs = JSON.parse(savedPenConfigs);

      // colors 배열이 없거나 비어있으면 다시 설정
      const palette = this._colors.map((color) => color.hex);
      Object.values(this._penConfigs).forEach((config) => {
        if (!config.colors || config.colors.length === 0) {
          config.colors = palette.map((color) => ({ hex: color }));
        }
      });
    }

    this._uiStateService.isDrawingMode$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((isDrawingMode) => {
        this.isActive = isDrawingMode;

        if (this.isActive) {
          this._selectedTool =
            localStorage.getItem('selectedTool') ?? Tool.Pen1;

          this._applyTool();
        } else {
          this._selectedTool = undefined;
          this._selectedPenConfig = undefined;
        }
      });

    this._pdfDrawingService?.historyChange$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((data) => {
        this._canUndo = data.canUndo;
        this._canRedo = data.canRedo;
      });

    this._loadTextConfig();

    // 텍스트 객체 선택 이벤트 감지 추가
    this._pdfDrawingService?.textObjectSelected$
      ?.pipe(takeUntil(this.unsubscriber))
      .subscribe((textObjectData) => {
        if (textObjectData && this._selectedTool === Tool.Text) {
          this._handleTextObjectSelection(textObjectData);
        } else {
          this._clearTextObjectSelection();
        }
      });

    // 외부에서 툴 변경 이벤트 감지 추가
    this._pdfDrawingService?.toolChanged$
      ?.pipe(takeUntil(this.unsubscriber))
      .subscribe((data) => {
        if (!data) {
          return;
        }

        const { tool } = data;

        if (tool === 'pen') {
          // 컬러 피커와 관련 상태 정리
          this._isColorPickerOpened = false;
          this._selectedTextConfig = undefined;

          // UI 상태만 업데이트 (실제 툴 적용은 하지 않음)
          this._selectedTool = Tool.Pen1;
          localStorage.setItem('selectedTool', Tool.Pen1);

          this._applyTool();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  get _isRulerOn(): boolean {
    if (
      this._selectedTool !== Tool.Pen1 &&
      this._selectedTool !== Tool.Pen2 &&
      this._selectedTool !== Tool.Pen3 &&
      this._selectedTool !== Tool.Pen4
    ) {
      return false;
    }

    return this._penConfigs[this._selectedTool].isStraightMode;
  }

  _onSelectTool(tool: Tool): void {
    if (
      tool === Tool.Pen1 ||
      tool === Tool.Pen2 ||
      tool === Tool.Pen3 ||
      tool === Tool.Pen4
    ) {
      this._selectedTextObjectConfig = undefined;

      if (this._selectedTool === tool) {
        if (this._selectedPenConfig) {
          this._selectedPenConfig = undefined;
        } else {
          this._selectedPenConfig = this._penConfigs[tool];
        }
      } else {
        this._selectedPenConfig = undefined;
      }
    } else if (tool === Tool.Eraser) {
      this._selectedPenConfig = undefined;
      this._selectedTextObjectConfig = undefined;
    } else if (tool === Tool.Text) {
      // 텍스트 관련 알림 노출 (최초 한번만 노출)
      if (localStorage.getItem('text-toast-shown') !== 'true') {
        this._toastService.open(
          '텍스트 입력을 원하는 곳을 탭하세요',
          undefined,
          {
            duration: 2000,
          }
        );

        localStorage.setItem('text-toast-shown', 'true');
      }

      if (this._selectedTool === tool) {
        if (this._isTextObjectSelected) {
          // this._selectedTextObjectConfig = this._textConfig;
          this._isTextObjectSelected = false;
        } else {
          if (!this._selectedTextObjectConfig) {
            this._selectedTextObjectConfig = this._textConfig;
          }
          this._isTextObjectSelected = true;
        }
      } else {
        this._selectedPenConfig = undefined;
        this._isTextObjectSelected = false;
      }

      // 텍스트 도구 선택 시 기존 텍스트 설정 유지
      // this._textConfig는 변경하지 않음
    }

    // 선택된 텍스트 객체가 있는 경우 color picker 상태 유지
    if (
      !this._selectedPenConfig &&
      !this._selectedTextConfig &&
      !this._isTextObjectSelected
    ) {
      this._isColorPickerOpened = false;
    }

    // 텍스트 필기 도구 선택시 처음 선택에만 applyTool이 호출되게 하기 위한 분기처리
    if (this._selectedTool === 'text' && tool === 'text') {
      return;
    }

    this._selectedTool = tool;

    if (this._selectedTool !== Tool.Eraser) {
      localStorage.setItem('selectedTool', tool);
    }

    this._applyTool();
  }

  _toggleRuler(): void {
    if (
      this._selectedTool !== Tool.Pen1 &&
      this._selectedTool !== Tool.Pen2 &&
      this._selectedTool !== Tool.Pen3 &&
      this._selectedTool !== Tool.Pen4
    ) {
      return;
    }

    this._penConfigs[this._selectedTool].isStraightMode =
      !this._penConfigs[this._selectedTool].isStraightMode;

    this._applyTool();
  }

  _onClearButtonClick(): void {
    this._pdfDrawingService?.clear();
  }

  _onCloseButtonClick(event?: PointerEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // 드로잉 서비스의 툴을 선택 모드로 변경
    this._pdfDrawingService?.setTool('marquee');

    // 드로잉 모드를 비활성화
    this._uiStateService.isDrawingMode = false;
  }

  _onPenColorClick(index: number, color: { hex: string }): void {
    if (this._selectedPenConfig!.colorIndex === index) {
      return;
    }

    // 색 선택의 결과가 바로 펜 색에 반영되게 하는 부분
    this._penConfigs[`pen${this._selectedPenConfig?.id}`].color = color.hex;

    this._selectedPenConfig!.colorIndex = index;
    this._onPenConfigChanged();
  }

  _onPenConfigChanged(): void {
    if (!this._selectedTool) {
      return;
    }

    this._applyTool();
  }

  private _applyTool(): void {
    if (this._selectedTool === Tool.Selection) {
      this._pdfDrawingService?.setTool('marquee');
    } else if (this._selectedTool === Tool.Eraser) {
      this._pdfDrawingService?.setTool('eraser');
    } else if (this._selectedTool === Tool.Text) {
      this._pdfDrawingService?.setTool('text', {
        color:
          this._textConfig.colors[this._textConfig.colorIndex]?.hex ||
          this._textConfig.color,
        width: this._textConfig.fontSize,
        straight: false,
        fontFamily: this._textConfig.fontFamily,
      });
    } else if (
      this._selectedTool === Tool.Pen1 ||
      this._selectedTool === Tool.Pen2 ||
      this._selectedTool === Tool.Pen3 ||
      this._selectedTool === Tool.Pen4
    ) {
      this._pdfDrawingService?.setTool('pen', {
        width: this._penConfigs[this._selectedTool].width,
        color:
          this._penConfigs[this._selectedTool].colors[
            this._penConfigs[this._selectedTool].colorIndex
          ].hex + alphaToHex(this._penConfigs[this._selectedTool].opacity),
        straight: this._penConfigs[this._selectedTool].isStraightMode,
      });

      // 내가 선택한 요소들에 대한 설정을 로컬스토리지에 저장
      localStorage.setItem('penConfigs-new', JSON.stringify(this._penConfigs));
    }
  }

  _onUndoButtonClick(): void {
    this._canUndo && this._pdfDrawingService?.undo();
  }

  _onRedoButtonClick(): void {
    this._canRedo && this._pdfDrawingService?.redo();
  }

  _colorChanged(index: number, color: string): void {
    this._colors[index].hex = color;

    if (this._selectedPenConfig) {
      // 팔레트 색상 변경 시 펜 색상 변경
      this._penConfigs[`pen${this._selectedPenConfig.id}`].colors[index].hex =
        color;
      // 색 선택의 결과가 바로 펜 색에 반영되게 하는 부분
      this._penConfigs[`pen${this._selectedPenConfig.id}`].color = color;
    }

    // 텍스트 설정에도 반영
    this._textConfig.colors[index].hex = color;
    if (this._textConfig.colorIndex === index) {
      this._textConfig.color = color;
      // 텍스트 설정 저장
      localStorage.setItem('textConfig', JSON.stringify(this._textConfig));
    }
  }

  _onTextColorClick(index: number, color: { hex: string }): void {
    // 이미 선택된 색상을 클릭한 경우, 컬러피커가 자동으로 열림 (HTML의 cdkMenuTriggerFor에 의해)
    if (this._textConfig.colorIndex === index) {
      // 컬러피커 열림을 위해 아무것도 하지 않음
      return;
    }

    // 다른 색상을 클릭한 경우, 해당 색상으로 변경
    this._textConfig.colorIndex = index;
    this._textConfig.color = color.hex;
    this._applyTool();
    // 텍스트 설정 저장
    localStorage.setItem('textConfig', JSON.stringify(this._textConfig));
  }

  _onTextFontSizeChange(fontSize: number): void {
    this._textConfig.fontSize = fontSize;
    this._applyTool();
  }

  _onTextFontFamilyChange(fontFamily: string): void {
    this._textConfig.fontFamily = fontFamily;
    this._applyTool();
  }

  _onTextConfigChanged(): void {
    // 현재 텍스트 설정을 localStorage에 저장
    localStorage.setItem(
      'textConfig',
      JSON.stringify({
        fontSize: this._textConfig.fontSize,
        colorIndex: this._textConfig.colorIndex,
        fontFamily: this._textConfig.fontFamily,
        color: this._textConfig.color,
      })
    );

    // PDF 드로잉 서비스에 업데이트된 텍스트 설정 전달
    if (this._pdfDrawingService) {
      this._pdfDrawingService.setTool('text', {
        color: this._textConfig.color,
        width: this._textConfig.fontSize, // fontSize를 width로 전달
        fontFamily: this._textConfig.fontFamily,
      } as any);
    }
  }

  _getInitialColorForPicker(index: number): string {
    // 텍스트 도구가 선택된 경우 텍스트 색상 반환
    if (this._selectedTool === Tool.Text) {
      return (
        this._textConfig.colors[index]?.hex ||
        this._colors[index]?.hex ||
        '#000000'
      );
    }

    // 펜 도구가 선택된 경우 펜 색상 반환
    if (this._selectedPenConfig) {
      return (
        this._penConfigs[this._selectedTool!].colors[index]?.hex ||
        this._colors[index]?.hex ||
        '#000000'
      );
    }

    // 기본값
    return this._colors[index]?.hex || '#000000';
  }

  _colorPickerClosed(): void {
    this._applyTool();
    this._savePalette();

    // 텍스트 설정이 변경된 경우 저장
    if (this._selectedTextConfig) {
      localStorage.setItem('textConfig', JSON.stringify(this._textConfig));
    }

    // 선택된 텍스트 객체가 있고 Selection 모드가 아닌 경우에만 해제
    if (this._isTextObjectSelected && this._selectedTool !== Tool.Selection) {
      this._clearTextObjectSelection();
    }
  }

  _onToolBarDragStarted(): void {
    this.colorPickerMenuTriggers?.forEach((trigger) => trigger.close());
  }

  // toolbar 이동중 위치를 계산해서 toolbar position을 체크하는 메서드
  _onToolBarDragMoved(event: CdkDragMove): void {
    const toolBarRect =
      event.source.element.nativeElement.getBoundingClientRect();
    const toolBarCenterX = toolBarRect.left + toolBarRect.width / 2;

    this.checkToolbarPosition(toolBarCenterX);
  }

  // toolbar 이동하다 손을 뗏을때 toolbar position을 체크하는 메서드
  _onToolBarDragEnded(event: CdkDragEnd): void {
    const toolBarRect =
      event.source.element.nativeElement.getBoundingClientRect();
    const toolBarCenterX = toolBarRect.left + toolBarRect.width / 2;

    this.checkToolbarPosition(toolBarCenterX);
  }

  // 현재 toolbar의 위치를 확인하고, 중앙을 기준으로 왼쪽, 오른쪽, 중앙으로 위치를 설정
  private checkToolbarPosition(currentX: number): void {
    const viewportCenterX = window.innerWidth / 2;

    if (currentX < viewportCenterX) {
      this.toolbarPosition = 'left';
    } else if (currentX > viewportCenterX) {
      this.toolbarPosition = 'right';
    } else {
      this.toolbarPosition = 'center';
    }
  }

  _onChangePageButtonClick(direction: 1 | -1): void {
    this._eventBusService.fire('DrawingToolBarComponent:pageChange', {
      direction: direction === 1 ? Direction.Next : Direction.Prev,
    });
  }

  private _savePalette(): void {
    localStorage.setItem(
      PALETTE_LOCALSTORAGE_KEY,
      JSON.stringify(this._colors.map(({ hex }) => hex))
    );
  }

  private _loadPalette(): boolean {
    const saved = localStorage.getItem(PALETTE_LOCALSTORAGE_KEY);
    let palette: string[];

    if (!saved) {
      palette = DEFAULT_PALETTE;
    } else {
      try {
        palette = JSON.parse(saved);
      } catch (error) {
        palette = DEFAULT_PALETTE;
      }
    }

    this._colors = palette.map((color) => ({ hex: color }));
    this._penConfigs[Tool.Pen1].colors = palette.map((color) => ({
      hex: color,
    }));
    this._penConfigs[Tool.Pen2].colors = palette.map((color) => ({
      hex: color,
    }));
    this._penConfigs[Tool.Pen3].colors = palette.map((color) => ({
      hex: color,
    }));
    this._penConfigs[Tool.Pen4].colors = palette.map((color) => ({
      hex: color,
    }));

    // 텍스트 설정에도 팔레트 적용
    this._textConfig.colors = palette.map((color) => ({ hex: color }));

    return !!saved;
  }

  private _loadTextConfig(): void {
    // localStorage 설정을 로드하지 않고 항상 기본값 사용
    this._textConfig = {
      fontSize: 20,
      colorIndex: 0,
      fontFamily: 'Noto Sans KR',
      color: '#000000',
      colors: this._colors,
    };

    // 또는 기존 로직을 유지하되 기본값만 변경하려면:
    // const saved = localStorage.getItem('textConfig');
    // if (saved) {
    //   try {
    //     const savedConfig = JSON.parse(saved);
    //     this._textConfig = {
    //       ...this._textConfig,
    //       ...savedConfig,
    //       colors: this._textConfig.colors,
    //     };
    //   } catch (error) {
    //     console.warn('Failed to load text config from localStorage:', error);
    //   }
    // }
  }

  private rgbToHex = (r: any, g: any, b: any): string =>
    '#' +
    [r, g, b]
      .map((n) => n.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();

  private rgbStringToHex = (rgbStr: any): any => {
    const [r, g, b] = rgbStr.match(/\d+/g).map(Number);
    return this.rgbToHex(r, g, b);
  };

  // 텍스트 객체 선택 처리
  private _handleTextObjectSelection(textObjectData: any): void {
    let currentColor = '';

    // 선택된 텍스트의 현재 색상을 기반으로 색상 인덱스 찾기
    if (textObjectData.color && textObjectData.color.includes('rgb')) {
      currentColor = this.rgbStringToHex(textObjectData.color) || '#021523';
    } else {
      currentColor = textObjectData.color || '#021523';
    }
    const colorIndex = this._colors.findIndex(
      (color) => color.hex === currentColor
    );

    // 새로운 텍스트 객체 설정
    this._selectedTextObjectConfig = {
      fontSize: textObjectData.fontSize || 20,
      colorIndex: colorIndex >= 0 ? colorIndex : 0,
      fontFamily: textObjectData.fontFamily || 'Noto Sans KR',
      color: currentColor,
      colors: this._colors,
    };
  }

  // 텍스트 객체 선택 해제 조건 수정
  private _clearTextObjectSelection(): void {
    // this._isTextObjectSelected = false;
    // this._selectedTextObjectConfig = undefined;
  }

  // 선택된 텍스트 객체의 색상 변경
  _onSelectedTextColorClick(index: number, color: { hex: string }): void {
    if (!this._selectedTextObjectConfig) {
      return;
    }

    // 이미 선택된 색상을 클릭한 경우, 컬러피커가 자동으로 열림
    if (this._selectedTextObjectConfig.colorIndex === index) {
      return;
    }

    // 다른 색상을 클릭한 경우, 해당 색상으로 변경
    this._selectedTextObjectConfig.colorIndex = index;
    this._selectedTextObjectConfig.color = color.hex;
    this._textConfig.colorIndex = index;
    this._textConfig.color = color.hex;

    // PDF Drawing Service를 통해 실제 텍스트 객체의 색상 변경
    this._pdfDrawingService?.updateSelectedTextColor?.(color.hex);
    this._applyTool();
  }

  // 선택된 텍스트 객체의 색상 변경 (컬러피커에서)
  _onSelectedTextColorChanged(index: number, color: string): void {
    if (!this._selectedTextObjectConfig) {
      return;
    }

    this._colors[index].hex = color;
    this._selectedTextObjectConfig.colors[index].hex = color;
    this._selectedTextObjectConfig.color = color;

    // PDF Drawing Service를 통해 실제 텍스트 객체의 색상 변경
    this._pdfDrawingService?.updateSelectedTextColor?.(color);
  }

  // 선택된 텍스트 객체의 폰트 크기 변경
  _onSelectedTextFontSizeChanged(): void {
    if (!this._selectedTextObjectConfig) {
      return;
    }

    // PDF Drawing Service를 통해 실제 텍스트 객체의 폰트 크기 변경
    this._pdfDrawingService?.updateSelectedTextFontSize?.(
      this._selectedTextObjectConfig.fontSize
    );
    this._textConfig.fontSize = this._selectedTextObjectConfig.fontSize;
    this._applyTool();
  }

  // 선택된 텍스트 객체의 폰트 패밀리 변경 메서드 추가
  _onSelectedTextFontFamilyChanged(): void {
    if (!this._selectedTextObjectConfig) {
      return;
    }

    // PDF Drawing Service를 통해 실제 텍스트 객체의 폰트 패밀리 변경
    this._pdfDrawingService?.updateSelectedTextFontFamily?.(
      this._selectedTextObjectConfig.fontFamily
    );
    this._textConfig.fontFamily = this._selectedTextObjectConfig.fontFamily;
    this._applyTool();
  }

  toggleFontDropdown(): void {
    this.isFontDropdownOpen = !this.isFontDropdownOpen;
  }

  selectFont(fontFamily: string, type: 'selected' | 'non-selected'): void {
    if (type === 'selected') {
      this._selectedTextObjectConfig!.fontFamily = fontFamily;
      this._onSelectedTextFontFamilyChanged();
    } else {
      this._textConfig.fontFamily = fontFamily;
      this._onTextConfigChanged();
    }
    this.isFontDropdownOpen = false;
  }

  // 드롭다운 외부 클릭 시 닫기 (필요시 추가)
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.font-dropdown')) {
      this.isFontDropdownOpen = false;
    }
  }
}
